import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  FiArrowRight,
  FiDownload,
  FiInfo,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import { payoutApi } from "./api/payoutApiClient";
import {
  BULK_UPLOAD_SCHEMA_VERSION,
  allowedBeneficiaryAccountTypes,
  allowedBeneficiaryCategories,
  beneficiaryBulkUploadFields,
  buildHeaderAliases,
} from "./api/payoutBulkUploadSchemas";
import {
  compactBody,
  pickString,
  stableIdempotencyKey,
  writeSessionJson,
} from "./api/payoutMutationHelpers";
import {
  isValidBankAccountNumber,
  isValidEmail,
  isValidIfsc,
  isValidIndianMobile,
  normalizeBankAccountNumber,
  normalizeIfsc,
  normalizeIndianMobile,
} from "./api/payoutValidation";
import { showPayoutNotification } from "./PayoutNotification";

const sampleFilePath = "/payout-beneficiary-bulk-upload-sample.csv";
const headerAliases = buildHeaderAliases(beneficiaryBulkUploadFields);

const normalizeLookup = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const canonicalizeRow = (row: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [
      headerAliases[normalizeLookup(key)] || key,
      typeof value === "string" ? value.trim() : value,
    ]),
  );

const parseBulkFile = async (file: File) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) return [];
  return XLSX.utils
    .sheet_to_json<Record<string, unknown>>(sheet, { defval: "", raw: false })
    .map(canonicalizeRow)
    .filter((row) => Object.values(row).some((value) => String(value ?? "").trim()));
};

const resolveFundingAccountId = (
  row: Record<string, unknown>,
  accounts: Record<string, unknown>[],
) => {
  const rawId = pickString(row, ["funding_account_id", "payout_account_id"]);
  if (rawId && accounts.some((account) => pickString(account, ["account_id", "accountId", "id"]) === rawId)) {
    return rawId;
  }

  const normalizedName = normalizeLookup(
    pickString(row, ["funding_account_name", "payout_account_id", "funding_account_id"]),
  );
  const matched = accounts.find((account) => {
    const candidates = [
      pickString(account, ["account_id", "accountId", "id"]),
      pickString(account, ["display_name", "displayName", "name", "account_name", "accountName"]),
      pickString(account, ["bank_name", "bankName", "bank"]),
      pickString(account, ["account_number", "accountNumber", "account_number_last4", "accountNumberLast4"]),
    ];
    return candidates.some((candidate) => normalizeLookup(candidate) === normalizedName);
  });

  if (matched) return pickString(matched, ["account_id", "accountId", "id"]);
  const activeAccount =
    accounts.find(
      (account) => pickString(account, ["status"], "ACTIVE").toUpperCase() === "ACTIVE",
    ) || accounts[0];
  return activeAccount
    ? pickString(activeAccount, ["account_id", "accountId", "id"])
    : rawId;
};

const resolveRows = (
  rows: Record<string, unknown>[],
  accounts: Record<string, unknown>[],
) =>
  rows.map((row) => ({
    ...row,
    funding_account_id: resolveFundingAccountId(row, accounts),
    funding_account_name: pickString(row, ["funding_account_name"]) ||
      pickString(
        accounts.find(
          (account) =>
            pickString(account, ["account_id", "accountId", "id"]) ===
            resolveFundingAccountId(row, accounts),
        ),
        ["display_name", "displayName", "name", "account_name", "accountName"],
      ),
    name: pickString(row, ["beneficiary_name", "name"]),
    category: pickString(row, ["category"], "vendor"),
    mobile: pickString(row, ["mobile_number", "mobile"]),
    email: pickString(row, ["email"]),
    account_number: normalizeBankAccountNumber(pickString(row, ["account_number", "accountNumber"])),
    ifsc: normalizeIfsc(pickString(row, ["ifsc_code", "ifsc", "ifscCode"])),
    bank_name: pickString(row, ["bank_name", "bankName"]),
    account_type: pickString(row, ["account_type", "accountType"], "savings"),
    branch_name: pickString(row, ["branch_name", "branch"]),
    remarks: pickString(row, ["remarks"]),
  }));

const validationPayloadRow = (row: Record<string, unknown>) =>
  compactBody({
    funding_account_id: pickString(row, [
      "funding_account_id",
      "fundingAccountId",
      "payout_account_id",
      "account_id",
    ]),
    name: pickString(row, ["name", "name_entered", "beneficiary_name"]),
    category: pickString(row, ["category"], "vendor"),
    mobile: normalizeIndianMobile(pickString(row, ["mobile", "mobile_number"])),
    email: pickString(row, ["email"]),
    account_number: normalizeBankAccountNumber(pickString(row, ["account_number", "accountNumber"])),
    ifsc: normalizeIfsc(pickString(row, ["ifsc", "ifsc_code", "ifscCode"])),
    account_type: pickString(row, ["account_type", "accountType"], "savings"),
    bank_name: pickString(row, ["bank_name", "bankName"]),
    branch_name: pickString(row, ["branch_name", "branchName", "branch"]),
    remarks: pickString(row, ["remarks", "purpose"]),
  });

const normalizeOption = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, "-");

const validateParsedRows = (rows: Record<string, unknown>[]) => {
  const errors: string[] = [];
  const seenAccountKeys = new Map<string, number>();

  rows.forEach((row, index) => {
    const rowNumber = index + 1;
    const fundingAccountId = pickString(row, ["funding_account_id"]);
    const name = pickString(row, ["name", "beneficiary_name", "name_entered"]);
    const accountNumber = normalizeBankAccountNumber(pickString(row, ["account_number", "accountNumber"]));
    const ifsc = normalizeIfsc(pickString(row, ["ifsc", "ifsc_code", "ifscCode"]));
    const email = pickString(row, ["email"]);
    const mobile = normalizeIndianMobile(pickString(row, ["mobile", "mobile_number"]));
    const category = normalizeOption(pickString(row, ["category"], "vendor"));
    const accountType = normalizeOption(pickString(row, ["account_type", "accountType"], "savings"));

    if (!fundingAccountId) errors.push(`Row ${rowNumber}: payout account not found`);
    if (!name) errors.push(`Row ${rowNumber}: beneficiary name is required`);
    if (!isValidBankAccountNumber(accountNumber)) {
      errors.push(`Row ${rowNumber}: account number must be 9-18 digits`);
    }
    if (!isValidIfsc(ifsc)) {
      errors.push(`Row ${rowNumber}: IFSC is invalid`);
    }
    if (email && !isValidEmail(email)) {
      errors.push(`Row ${rowNumber}: email is invalid`);
    }
    if (mobile && !isValidIndianMobile(mobile)) {
      errors.push(`Row ${rowNumber}: mobile number must be a valid 10-digit Indian mobile number`);
    }
    if (category && !allowedBeneficiaryCategories.has(category)) {
      errors.push(`Row ${rowNumber}: category is invalid`);
    }
    if (accountType && !allowedBeneficiaryAccountTypes.has(accountType)) {
      errors.push(`Row ${rowNumber}: account type is invalid`);
    }

    if (accountNumber && ifsc) {
      const key = `${accountNumber}|${ifsc}`;
      const firstSeen = seenAccountKeys.get(key);
      if (firstSeen !== undefined) {
        errors.push(`Row ${rowNumber}: duplicate account and IFSC also found in row ${firstSeen}`);
      } else {
        seenAccountKeys.set(key, rowNumber);
      }
    }
  });

  return errors;
};

const validationErrorMessage = (errors: string[]) => {
  const visible = errors.slice(0, 5).join("; ");
  return errors.length > 5 ? `${visible}; +${errors.length - 5} more` : visible;
};

const buildDatabaseSampleRows = (accounts: Record<string, unknown>[]) => {
  const activeAccount =
    accounts.find(
      (account) => pickString(account, ["status"], "ACTIVE").toUpperCase() === "ACTIVE",
    ) || accounts[0];
  if (!activeAccount) {
    throw new Error("Add at least one active payout account before using sample beneficiary data.");
  }

  const fundingAccountId = pickString(activeAccount, ["account_id", "accountId", "id"]);
  const fundingAccountName = pickString(activeAccount, [
    "display_name",
    "displayName",
    "name",
    "account_name",
    "accountName",
  ]);

  const sampleRows = [
    {
      name: "ABC Stationery Pvt Ltd",
      category: "vendor",
      mobile: "9876543210",
      email: "accounts@abcstationery.in",
      account_number: "123456789012",
      ifsc: "HDFC0004589",
      bank_name: "HDFC Bank",
      account_type: "savings",
      branch_name: "Connaught Place",
      remarks: "Monthly stationery vendor",
    },
    {
      name: "Priya Verma",
      category: "employee",
      mobile: "9123456780",
      email: "priya.verma@clearn.in",
      account_number: "903456781234",
      ifsc: "ICIC0009034",
      bank_name: "ICICI Bank",
      account_type: "savings",
      branch_name: "Bandra",
      remarks: "Employee reimbursement",
    },
    {
      name: "Aarav Sharma",
      category: "student",
      mobile: "9988766554",
      email: "aarav.sharma@student.in",
      account_number: "112344556677",
      ifsc: "UTIB0001123",
      bank_name: "Axis Bank",
      account_type: "savings",
      branch_name: "Noida",
      remarks: "Scholarship disbursal",
    },
    {
      name: "Smart Class Solutions",
      category: "vendor",
      mobile: "8765432109",
      email: "billing@smartclass.in",
      account_number: "778133445566",
      ifsc: "SBIN0007781",
      bank_name: "State Bank of India",
      account_type: "current",
      branch_name: "Indiranagar",
      remarks: "Smart classroom services",
    },
    {
      name: "City Transport Services",
      category: "transport-vendor",
      mobile: "9012345678",
      email: "finance@citytransport.in",
      account_number: "556677889900",
      ifsc: "PUNB0015300",
      bank_name: "Punjab National Bank",
      account_type: "current",
      branch_name: "Karol Bagh",
      remarks: "Transport vendor payment",
    },
    {
      name: "Utility Power Board",
      category: "utility-provider",
      mobile: "9876501234",
      email: "billing@utilitypower.in",
      account_number: "667788990011",
      ifsc: "KKBK0000958",
      bank_name: "Kotak Mahindra Bank",
      account_type: "savings",
      branch_name: "MG Road",
      remarks: "Utility payment",
    },
  ];

  return sampleRows.map((row) => ({
    schema_version: BULK_UPLOAD_SCHEMA_VERSION,
    funding_account_id: fundingAccountId,
    funding_account_name: fundingAccountName,
    ...row,
  }));
};

function PayoutBeneficiaryBulkUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [sampleSelected, setSampleSelected] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!error) return;
    showPayoutNotification({
      tone: "error",
      title: "Bulk beneficiary upload failed",
      message: error,
    });
  }, [error]);

  const chooseFile = (selectedFile?: File | null) => {
    if (!selectedFile) return;
    const validExtension = /\.(csv|xlsx)$/i.test(selectedFile.name);
    const validSize = selectedFile.size <= 10 * 1024 * 1024;

    if (!validExtension) {
      setError("Upload a CSV or XLSX file.");
      setFile(null);
      return;
    }
    if (!validSize) {
      setError("File size must be 10 MB or less.");
      setFile(null);
      return;
    }

    setError("");
    setSampleSelected(false);
    setFile(selectedFile);
  };

  const useSampleFile = () => {
    setError("");
    setSampleSelected(true);
    setFile(
      new File(["sample"], "payout-beneficiary-bulk-upload-sample.csv", {
        type: "text/csv",
      }),
    );
  };

  const uploadFile = async () => {
    if (!file) {
      setError("Bulk Upload Beneficiaries: upload a beneficiary file or use the sample template before proceeding.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const accounts = await payoutApi.accounts();
      const parsedRows = sampleSelected
        ? buildDatabaseSampleRows(accounts)
        : resolveRows(await parseBulkFile(file), accounts);
      if (!parsedRows.length) {
        throw new Error("No beneficiary rows found in this file.");
      }
      const localErrors = validateParsedRows(parsedRows);
      if (localErrors.length) {
        throw new Error(
          `Fix the beneficiary template fields before continuing. ${validationErrorMessage(localErrors)}`,
        );
      }
      const validation = await payoutApi.validateBulkBeneficiaries(
        { items: parsedRows.map(validationPayloadRow) },
        stableIdempotencyKey("validate-bulk-beneficiaries-upload", file.name),
      );
      const apiErrors = Array.isArray(validation.results)
        ? validation.results.flatMap((result) => {
            const rowResult = result as Record<string, unknown>;
            if (rowResult.ok !== false) return [];
            const rowNumber = typeof rowResult.index === "number" ? rowResult.index + 1 : "-";
            const errors = Array.isArray(rowResult.errors)
              ? rowResult.errors.filter((error): error is string => typeof error === "string")
              : ["Validation failed"];
            return errors.map((error) => `Row ${rowNumber}: ${error}`);
          })
        : [];
      if (apiErrors.length) {
        throw new Error(
          `Payouts API rejected the beneficiary batch. ${validationErrorMessage(apiErrors)}`,
        );
      }
      writeSessionJson("payoutBulkBeneficiaryDraft", {
        fileName: file.name,
        sizeKb: Math.max(1, Math.round(file.size / 1024)),
        rows: parsedRows,
      });
      showPayoutNotification({
        tone: "success",
        title: "Beneficiary file ready",
        message: `${parsedRows.length} beneficiaries passed validation. Review them before verification.`,
      });
      navigate("/payouts/beneficiaries/bulk-upload/review");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to read this bulk upload file.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Bulk Upload Beneficiaries
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Upload a CSV or XLSX file to add multiple beneficiaries.
          </p>
        </div>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={1} />

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx"
          className="hidden"
          onChange={(event) => chooseFile(event.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            chooseFile(event.dataTransfer.files?.[0]);
          }}
          className="w-full rounded-[8px] border border-dashed border-[#A3AED0] bg-white px-6 py-12 text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-[8px] bg-[#6687FF1A] text-[#6687FF]">
            <FiUploadCloud className="text-3xl" />
          </div>
          <p className="mt-5 text-sm font-semibold text-[#1B163B]">
            Drag and drop your file here or{" "}
            <span className="text-[#6687FF]">click to upload</span>
          </p>
          <p className="mt-2 text-xs font-medium text-[#505E8E]">
            Supported formats: CSV, XLSX. Maximum file size 10 MB.
          </p>
        </button>

        {false && (
          <div className="mt-4 rounded-[6px] border border-[#EF444433] bg-[#FEE2E2] px-4 py-3 text-sm font-semibold text-[#EF4444]">
            {error}
          </div>
        )}

        {file && (
          <div className="mt-4 flex items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]">
                <LuFileText />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1B163B]">
                  {file.name}
                </p>
                <p className="text-xs font-medium text-[#505E8E]">
                  {Math.max(1, Math.round(file.size / 1024))} KB
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setFile(null);
                setSampleSelected(false);
              }}
              className="text-[#505E8E]"
            >
              <FiX className="text-lg" />
            </button>
          </div>
        )}

        <div className="mt-5 rounded-[6px] bg-[#6687FF1A] px-4 py-4 text-sm font-medium text-[#6687FF]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <FiInfo />
              Use beneficiary schema v{BULK_UPLOAD_SCHEMA_VERSION} to avoid validation errors.
            </span>
            <span className="flex items-center gap-4">
              <button
                type="button"
                onClick={useSampleFile}
                className="inline-flex items-center gap-2 font-semibold"
              >
                <LuFileText />
                Use sample data
              </button>
              <a
                href={sampleFilePath}
                download
                className="inline-flex items-center gap-2 font-semibold"
              >
                <FiDownload />
                Download sample
              </a>
            </span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-4">
          <Link
            to="/payouts/beneficiaries"
            className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={uploadFile}
            disabled={uploading}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
          >
            {uploading ? "Reading File" : "Upload File"}
            <FiArrowRight />
          </button>
        </div>
      </section>
    </div>
  );
}

export default PayoutBeneficiaryBulkUpload;
